import axios, { AxiosError } from "axios";
import {
  buildStorefrontLoginRedirectPath,
  isProtectedStorefrontAccountPath,
  isStorefrontAuthPath,
} from "@/lib/auth-routing";

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:5193";

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  return config;
});

type RetryableRequestConfig = NonNullable<AxiosError["config"]> & { _retry?: boolean };

function buildLoginRedirectUrl(): string {
  return buildStorefrontLoginRedirectPath(window.location.pathname, window.location.search);
}

function isAuthPageLocation(): boolean {
  return typeof window !== "undefined" && isStorefrontAuthPath(window.location.pathname);
}

function isProtectedPageLocation(): boolean {
  return typeof window !== "undefined" && isProtectedStorefrontAccountPath(window.location.pathname);
}

api.interceptors.response.use(
  (res) => res,
  (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (error.response?.status === 401 && typeof window !== "undefined" && originalRequest) {
      const url = originalRequest.url ?? "";
      const isAuthEndpoint =
        url.includes("/api/account/login") ||
        url.includes("/api/account/login-external") ||
        url.includes("/api/account/register") ||
        url.includes("/api/account/refresh") ||
        url.includes("/api/account/logout");
      const isOptionalSessionProbe = url.includes("/api/account/me");

      if (!isProtectedPageLocation() || isOptionalSessionProbe) {
        return Promise.reject(error);
      }

      if (!originalRequest._retry && !isAuthEndpoint) {
        originalRequest._retry = true;

        return api.post("/api/account/refresh", {})
          .then(() => api(originalRequest))
          .catch(() => {
            if (!isAuthPageLocation()) {
              window.location.href = buildLoginRedirectUrl();
            }
            return Promise.reject(error);
          });
      }

      if (!isAuthEndpoint && !isAuthPageLocation()) {
        window.location.href = buildLoginRedirectUrl();
      }
    }

    return Promise.reject(error);
  }
);

// Server-side fetch (Server Components için, axios değil native fetch)
export async function serverFetch<T>(path: string): Promise<T> {
  const url = `${process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:5193"}${path}`;

  const headers: HeadersInit = {};
  try {
    const { cookies } = await import("next/headers");
    const cookieStore = await cookies();
    const cookieHeader = cookieStore.toString();
    if (cookieHeader) {
      headers["Cookie"] = cookieHeader;
    }
  } catch {
    // Client context — no cookies to forward
  }

  const res = await fetch(url, { next: { revalidate: 10 }, headers });
  if (!res.ok) throw new Error(`${res.status} ${path}`);
  return res.json();
}

export default api;
